import * as React from 'react';
import { useMemo } from 'react';

import get from 'lodash/get';
import map from 'lodash/map';

import {
  Alert,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@sbv-clients/ui-shared';
import { createUniqueID } from '@sbv-clients/util-misc';

import { BaseTable } from './base-table';
import {
  employeeColumns,
  employeesRowKey,
  EmployeesRowType,
} from './employees-columns';
import { TabSelection } from './employees-stats-header';
import { useAppliedFilter } from './use-applied-filter';

export interface PropsType {
  tab: TabSelection;
  data: EmployeesRowType[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  groupByShift: boolean;
  filterByWeekend: boolean;
}

export function EmployeesGrid({
  tab,
  data,
  isLoading,
  isError,
  error,
  groupByShift,
  filterByWeekend,
}: PropsType) {
  const filterModel = useAppliedFilter(tab, filterByWeekend);
  const apiRef = useGridApiRef();
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      pagination: { paginationModel: { pageSize: 10 } },
      aggregation: {
        model: {
          [employeesRowKey('dismissed')]: 'sum',
          [employeesRowKey('interested')]: 'sum',
          [employeesRowKey('notified')]: 'sumBothNotified',
          [employeesRowKey('assigned')]: 'sumBothAssigned',
          [employeesRowKey('isActive')]: 'ratio',
          [employeesRowKey('isOnboarded')]: 'ratio',
          [employeesRowKey('isRegistered')]: 'ratio', // [rowKey('saasRole')]: 'noop',
        },
      },
    },
  });
  const rows = useMemo(
    () => map(data, (item) => ({ ...item, id: createUniqueID() })),
    [data]
  );
  if (isError) {
    return (
      <Alert severity={'error'}>
        {get(error, 'message', 'There was an error retrieving employees')}
      </Alert>
    );
  }

  return (
    <BaseTable
      rowGroupingModel={
        groupByShift ? [employeesRowKey('shiftSchedulerId')] : []
      }
      apiRef={apiRef}
      initialState={initialState}
      filterModel={filterModel}
      rows={rows}
      columns={employeeColumns}
      loading={isLoading}
    />
  );
}
